import { isNumber } from 'lodash-es';

import type { TruncationMode } from '@dynatrace/strato-components/typography';

import { CanvasCircleDisplay } from './CanvasCircleDisplay.js';
import { CanvasHexagonDisplay } from './CanvasHexagonDisplay.js';
import { CanvasSquareDisplay } from './CanvasSquareDisplay.js';
import type { Point } from '../../../../core/types/point.js';
import { getTokenComputedStyle } from '../../../../core/utils/getTokenComputedStyle.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import { HONEYCOMB_TILE_STROKE_COLOR } from '../../../constants.js';
import { useFillColorByHoverState } from '../../../hooks/use-fill-color-by-hover-state.js';
import { useHiveRenderingMetadata } from '../../../hooks/use-hive-rendering-metadata.js';
import { useHiveTileBestFit } from '../../../hooks/use-hive-tile-best-fit.js';
import { useHoneycombFormatterConfig } from '../../../hooks/use-honeycomb-formatter-config.js';
import { useHoneycombShape } from '../../../hooks/use-honeycomb-shape.js';
import { useHoneycombShowLabels } from '../../../hooks/use-honeycomb-show-labels.js';
import { useHoneycombTickFormatter } from '../../../hooks/use-honeycomb-tick-formatter.js';
import { useHoneycombTruncationMode } from '../../../hooks/use-honeycomb-truncation-mode.js';
import { HoneycombTileData } from '../../../types/honeycomb.js';

interface TileAbstractDisplayProps {
  data: HoneycombTileData;
}

export interface TileConcreteDisplayProps {
  center: Point;
  fillColor: string;
  width: number;
  radius: number;
  value: string | number;
  name?: string;
  strokeColor?: string;
  truncationMode?: TruncationMode;
  showLabels?: boolean;
}

export const TileAbstractDisplay = ({ data }: TileAbstractDisplayProps) => {
  const shape = useHoneycombShape();
  const { renderMetadata } = useHiveRenderingMetadata();
  const { tileWidth, gap } = useHiveTileBestFit();
  const { tileCenter, baseColor } = safeGet(renderMetadata, data);
  const { value, name } = data;
  const showLabels = useHoneycombShowLabels();
  const truncationMode = useHoneycombTruncationMode();

  const formatter = useHoneycombTickFormatter();
  const { unit } = useHoneycombFormatterConfig();

  const displayValue =
    isNumber(value) && formatter ? formatter(value, unit) : value;

  const fillColor = useFillColorByHoverState(data, baseColor);
  const strokeColor = getTokenComputedStyle(HONEYCOMB_TILE_STROKE_COLOR);
  const radius = Math.min(gap * 1.4, 12);

  const concreteDisplayProps: TileConcreteDisplayProps = {
    center: tileCenter,
    width: tileWidth,
    fillColor,
    strokeColor,
    radius,
    value: displayValue,
    name,
    showLabels,
    truncationMode,
  };

  switch (shape) {
    case 'circle':
      return <CanvasCircleDisplay {...concreteDisplayProps} />;
    case 'square':
      return <CanvasSquareDisplay {...concreteDisplayProps} />;
    case 'hexagon':
      return <CanvasHexagonDisplay {...concreteDisplayProps} />;
  }
};

TileAbstractDisplay['displayName'] = 'TileAbstractDisplay';
